import { NavigateBackLink } from '@exam-center/components';
import { type RegistrationStatus } from '@exam-center/graphql';
import { Flex, PageHeader, Text } from '@shared/components';
import { formatDateTime } from '@shared/utils';

import {
  ExamEventRegistrationStatusLabel,
  type ExamEventRegistrationStatusLabelVariants,
} from '../../components';
import { REGISTRATION_TYPE_LABEL, EXAM_TYPE_LABEL } from '../../constants';
import { useExamEventDetails, type ExamEventData } from '../../providers';
import { getExamEventDetailsTitle, MONTH_ORDER } from '../../utils';
import { ExamEventDetailsNavigationTabs } from '../exam-event-details-navigation-tabs';

const EXAM_EVENT_REGISTRATION_STATUS_TO_EXAM_EVENT_LABEL_VARIANT: Record<
  RegistrationStatus,
  ExamEventRegistrationStatusLabelVariants
> = {
  OPEN_FOR_REGISTRATION: 'open-for-registration',
  REGISTRATION_CLOSED: 'registration-closed',
  SESSION_FULL: 'session-full',
  CANCELED: 'canceled',
  TEMPORARILY_CLOSED: 'temporarily-closed',
  SCHEDULED: 'scheduled',
};

function getExamEventHeaderDetails(examEvent: ExamEventData) {
  const examEventName = examEvent.name;
  const examEventDate = new Date(examEvent.examStartedAt);
  const monthAbbr = MONTH_ORDER[examEvent.examStartedAt.getMonth()];
  const year = examEventDate.getFullYear();
  const updatedAt = examEvent.updatedAt;

  const regStatus = examEvent.registrationStatus;
  const regStatusVariant = EXAM_EVENT_REGISTRATION_STATUS_TO_EXAM_EVENT_LABEL_VARIANT[regStatus];

  const roundNo = examEvent.roundNumber;

  return {
    examEventName,
    roundNo,
    month: monthAbbr,
    year,
    regStatus: regStatusVariant,
    updatedAt,
    regType: REGISTRATION_TYPE_LABEL[examEvent.registrationType],
    examType: EXAM_TYPE_LABEL[examEvent.examType],
  };
}

interface ExamEventDetailsHeaderProps {
  examCenterCode: string;
}

export default function ExamEventDetailsHeader(props: Readonly<ExamEventDetailsHeaderProps>) {
  const { examCenterCode } = props;
  const { examEvent } = useExamEventDetails();

  const { examEventName, roundNo, month, year, regStatus, updatedAt, examType, regType } =
    getExamEventHeaderDetails(examEvent);

  return (
    <PageHeader
      title={examEventName}
      endAdornment={<ExamEventRegistrationStatusLabel variant={regStatus} />}
      subtitleSection={
        <Flex
          sx={{
            justifyContent: 'space-between',
            flex: 1,
          }}
        >
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
            })}
          >
            <Text variant='body2'>{getExamEventDetailsTitle(year, month)}</Text>
            <Text variant='body2'>รอบสอบที่: {roundNo}</Text>
            <Text variant='body2'>Reg Type: {regType}</Text>
            <Text variant='body2'>Exam Type: {examType}</Text>
          </Flex>
          <Text variant='body2'>Last Modified: {formatDateTime(updatedAt)}</Text>
        </Flex>
      }
      linkAdornment={
        <NavigateBackLink
          to='/:examCenterCode/exam-events/years/:year/months/:month'
          params={{
            examCenterCode,
            year: year.toString(),
            month: month,
          }}
        />
      }
      tabs={<ExamEventDetailsNavigationTabs />}
    />
  );
}
